import type { ComponentType, SVGProps } from 'react';
import { Clock, Trash2, Ban } from 'lucide-react';
import type { ChatMessage } from '@multimod/shared';

interface Props {
  msg: ChatMessage;
  onTimeout: () => void;
  onDelete: () => void;
  onBan: () => void;
}

type Tone = 'warning' | 'destructive';

// #15 exceção §7b: 28×28 dentro de cluster hover de moderação. Hotkeys B/T/D
// são a via primária; mouse é fallback. Hit area do cluster cresce via hover
// surface (msg-row inteira).
const TONE_CLASS: Record<Tone, string> = {
  warning:
    'hover:bg-warning/10 hover:text-warning focus-visible:bg-warning/10 focus-visible:text-warning focus-visible:ring-warning',
  destructive:
    'hover:bg-destructive/10 hover:text-destructive focus-visible:bg-destructive/10 focus-visible:text-destructive focus-visible:ring-destructive',
};

interface ActionBtnProps {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  tone: Tone;
  onClick: () => void;
}

function ActionBtn({ Icon, label, tone, onClick }: ActionBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={
        'grid place-items-center w-7 h-7 rounded-md text-fg-muted transition-colors duration-fast focus-visible:outline-none focus-visible:ring-1 ' +
        TONE_CLASS[tone]
      }
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
    </button>
  );
}

// #55: alguns displayNames YouTube já começam com '@'. Stripa antes de prefixar
// pra evitar '@@nome' em aria-labels.
function handle(displayName: string): string {
  return '@' + displayName.replace(/^@+/, '');
}

export function MsgActions({ msg, onTimeout, onDelete, onBan }: Props) {
  const who = handle(msg.author.displayName);
  return (
    <div
      className="msg-actions opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-fast flex items-center gap-0.5"
      role="group"
      aria-label={`Ações de moderação para ${who}`}
    >
      <ActionBtn
        Icon={Clock}
        label={`Silenciar ${who} temporariamente`}
        tone="warning"
        onClick={onTimeout}
      />
      <ActionBtn Icon={Trash2} label="Apagar mensagem" tone="destructive" onClick={onDelete} />
      {/* Separador visual antes do ban: ação destrutiva "pesada" (#106
          simplificado — popover de ações secundárias fica pra quando elas
          existirem). */}
      <span aria-hidden="true" className="w-px h-4 bg-border mx-0.5" />
      <ActionBtn Icon={Ban} label={`Banir ${who}`} tone="destructive" onClick={onBan} />
    </div>
  );
}
